import { useEffect, useState } from 'react';

import commonGlobals from '@opendoor/shared-fe/commonGlobals';
import { getRailsEnv } from '@opendoor/shared-fe/railsEnv';

import { getHost } from 'buying/helpers/hosts';

const env = getRailsEnv();
const secrets = commonGlobals[env] ?? {};

export const WEB_API_URL = secrets.webApiUrl;
export const useStaging = WEB_API_URL?.includes('staging');
export const RAILS_ENV = useStaging ? 'staging' : env;
export type ENV = typeof RAILS_ENV;

export const FACEBOOK_APP_ID = secrets.facebookAppID;
export const GOOGLE_CLIENT_ID = secrets.googleClientID;
export const GOOGLE_MAPS_KEY = secrets.googleMaps;
export const RECEPTION_FE_URL = secrets.consumerFeUrl;
export const BUYER_URL = getHost();
export const ADMIN_CLIENT_UPLOAD_BUCKET = `opendoor-admin-client-upload-${
  RAILS_ENV === 'development' ? 'staging' : RAILS_ENV
}`;

export const CXA_EMAIL = 'contact@opendoor.com';
export const CXA_EMAIL_NEW = 'hello@opendoor.com';

export const AGENT_ACQ_EMAIL = 'agents@opendoor.com';
export const UPDATE_HOME_DETAILS_HOTLINE = '888-352-7075';
export const CONTACT_PHONE = '888-352-7075';

export const offerPollingTimeoutThreshold = RAILS_ENV === 'production' ? 90_600 : 120_000;
export const backendRefreshBlockDurationMS = 120_000;

export const DOMAIN = {
  production: 'https://consumer.opendoor.com',
  staging: 'https://consumer.simplersell.com',
  qa: 'https://consumer.simplersell.com',
  development: 'http://localhost:3011',
  test: 'https://consumer.opendoor.com',
}[RAILS_ENV];
export const HOMEPAGE_URL = {
  production: 'https://www.opendoor.com',
  staging: 'https://demo.simplersell.com',
  qa: 'https://demo.simplersell.com',
  development: 'http://localhost:3011/homepage-2019',
  test: 'https://www.opendoor.com',
}[RAILS_ENV];

export const AGENT_OFFER_PORTAL_URL = {
  production: 'https://opendoor.com/make-offer/start',
  staging: 'https://demo.simplersell.com/make-offer/start',
  qa: 'https://demo.simplersell.com/make-offer/start',
  development: 'https://demo.simplersell.com/make-offer/start',
  test: 'https://demo.simplersell.com/make-offer/start',
}[RAILS_ENV];

// The URL for the lower mortgage flow
export const LOWER_MORTGAGE_FLOW_URL = {
  production: 'https://app.lower.com',
  staging: 'https://lightspeed.partner-testing.lwreng.com',
  qa: 'https://lightspeed.partner-testing.lwreng.com',
  development: 'https://lightspeed.partner-testing.lwreng.com',
  test: 'https://lightspeed.partner-testing.lwreng.com',
}[RAILS_ENV];

// The brand URL
export const LOWER_MORTGAGE_URL = 'https://lower.com/go';

export const COOKIE_KEY = RAILS_ENV === 'development' ? 'XSRF-TOKEN' : 'XSRF-G-TOKEN';

export const NAVIGATION_HEIGHT = '52px';

// SSVA confirmation
export const PRODUCTION_SELLER_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSe4glJ1pVRnUZdnpHbMDywnF1t5FAnhyQrQ7Q338WGmuelQRg/viewform?usp=pp_url';
export const STAGING_SELLER_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSe64KWEV4fKnLlwTAAi8PtoV09SOXtCfHAYviEJK4PrjY8KEA/viewform?usp=pp_url';
export const AGENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLScrtekcBF70cbU8rzKBmUOnwjhxWilOXBQj3W7hJqQfwvtTUg/viewform?usp=pp_url';
export const PRODUCTION_ALA_EXPERIENCE_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSeQHDLKdpDmmwpcr5r5JARETAIFxICUth4UFsWFRFV-5uY8Dw/viewform?usp=pp_url';
export const STAGING_ALA_EXPERIENCE_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSff6cvFZr4iSmazrSORUD7d04RADQG-lVzbhqP3ZttCP_k_TA/viewform?usp=pp_url';
export const FLIP_TOKEN_FORM_ENTRY = 'entry.1387189579';
export const EMAIL_FORM_ENTRY = 'entry.1100801854';
export const NAME_FORM_ENTRY = 'entry.678167604';

export const MAPBOX_ACCESS_TOKEN =
  'pk.eyJ1Ijoib3BlbmRvb3IiLCJhIjoiWXJ2bTJiRSJ9.lsnjT9yTNyyVzKRIzqxnqg';
export const MAPBOX_MAP_STYLE = 'mapbox://styles/opendoor/cktkqasmc1gbe17pfo2cm7ybb';

export const CUSTOMER_UUID_QUERY_PARAM = 'customer';

export const HELLOSIGN_CLIENT_ID = secrets.hellosignClientID;

export const useBrowserCompatibilityData = () => {
  const [userAgent, setUserAgent] = useState<string>('');
  const [supportsNativeSmoothScroll, setSupportsNativeSmoothScroll] = useState<boolean>(false);

  // Sets variables once DOM has mounted to avoid SSR issues
  useEffect(() => {
    setUserAgent(window.navigator.userAgent);
    setSupportsNativeSmoothScroll('scrollBehavior' in document.documentElement.style);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on mount
  }, []);

  const isOpendoorAppEvoWebView = userAgent.indexOf('EvolvedWebView') > -1;
  const isOpendoorAppWebView =
    userAgent.indexOf('OpendoorAppWebView') > -1 && !isOpendoorAppEvoWebView;
  const isWebView = isOpendoorAppWebView || isOpendoorAppEvoWebView;
  return {
    /** True if the user is using chrome */
    IS_USING_CHROME: userAgent.includes('Chrome'),
    /**
     * Updated User Agent that OpendoorAppWebView/EvolvedWebView/${DeviceVersion}
     * This will not break any existing webview checks
     */
    isOpendoorAppEvoWebView,
    isOpendoorAppWebView,
    isWebView,
    /**
     * Whether the browser supports smooth scrolling on elements
     * See more here: https://caniuse.com/?search=scrollTo
     */
    supportsNativeSmoothScroll,
  };
};

/**
 * Checks that a value is not null nor undefined.
 *
 * Can be used for filtering null and undefined values.
 * ```ts
 * const x: (number | null)[]
 * const y: number[] = x.filter(notEmpty)
 * ```
 */
export const notEmpty = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

/**
 * Asserts that a value is not null nor undefined.
 * @throws Error if null or undefined
 */
export const assertNotEmpty = <T>(name: string, value: T | null | undefined): T => {
  if (notEmpty(value)) {
    return value;
  }

  throw new Error(`expected ${name} not to be empty, but was ${value}`);
};
